"use client";

import Image from "next/image";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import WishlistButton from "@components/atoms/WishlistButton";
import Link from "next/link";
import styles from "../BuyCarCard.module.scss";
import { BUYCARCARD_CONFIG } from "../BuyCarCard.data";
import { useRowWiseCardHeight } from "@hooks/useRowWiseCardHeight";
import { useRef, useEffect, useState } from "react";
import assetLoader from "@utils/internalImageLoder";
import { getLoginUrl, addCaptureIntentIfNeeded } from "@utils/getLoginUrl";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getCarAvailability } from "@utils/carAvailability";
import { PAGE_TYPES } from "@constants/pageTypes";
import { usePathname } from "next/navigation";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";

interface CardContentProps {
  brand_name: string
  model_name: string
  experts_rating: string
  min_price: number
  max_price: number
  min_on_road_price: number
  min_acko_drive_savings: number
  min_waiting_period: number
  min_emi_amount: number
  variant_count: number
  status: any
  fuel_types?: string[]
  transmission_types?: string[]
  isLoggedIn: boolean
  isInWishlist: boolean
  cityName: string
  handleSpinnerLoading?: (loading: boolean) => void
  onWishlistClick?: () => void
  handleRouteClick?: () => void
  model_slug: string
  collectionName: string,
  isCampaignPage: boolean,
  title?: any;
  onNavigate?: (slug: string) => void;
}

const CardContent = ({
  brand_name,
  model_name,
  model_slug,
  experts_rating,
  min_price,
  max_price,
  min_on_road_price,
  min_acko_drive_savings,
  min_emi_amount,
  variant_count,
  status,
  fuel_types = [],
  transmission_types = [],
  isLoggedIn,
  isInWishlist,
  cityName,
  collectionName,
  handleSpinnerLoading,
  handleRouteClick,
  title,
  isCampaignPage,
  onNavigate,
  onWishlistClick = () => { }
}: CardContentProps) => {
  const { getRowHeight, isMobile, recalculateHeights } = useRowWiseCardHeight();
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number>(0);
  const [pageName, setPageName] = useState<string>(PAGE_TYPES.BUY_HOMEPAGE);
  const pathname = usePathname();
  const { hideLoader } = useGlobalLoader();

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const pageLink = window.location.href;
      const pageNameValue = pageLink.includes("collection")
        ? PAGE_TYPES.COLLECTION_PAGE
        : PAGE_TYPES.BUY_HOMEPAGE;
      setPageName(pageNameValue);
    }
  }, []);

  useEffect(() => {
    if (!isMobile && contentRef.current) {
      const timeoutId = setTimeout(() => {
        recalculateHeights();
        const height = getRowHeight(contentRef.current);
        setMinHeight(height);
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [
    brand_name,
    model_name,
    experts_rating,
    min_price,
    min_emi_amount,
    variant_count,
    isLoggedIn,
    getRowHeight,
    isMobile,
    recalculateHeights,
  ]);

  const handleButtonclick = async () => {
    if (status === 'out_of_production' || status === 'non_sellable' || status === 'upcoming' || isLoggedIn) {

      if(isCampaignPage && onNavigate){
        onNavigate(model_slug);
        return;
      }

      handleRouteClick?.();
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageName,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_see_details",
          car_availability: getCarAvailability(status),
          vehicle_make: brand_name,
          vehicle_model: model_name,
          city_name: cityName,
          collection_name: collectionName,
        },
      });
    } else {
      handleSpinnerLoading?.(true);
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageName,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_login_to_save_mm",
          car_availability: getCarAvailability(status),
          vehicle_make: brand_name,
          vehicle_model: model_name,
          city_name: cityName,
          collection_name: collectionName,
        },
      });
      if (typeof window !== "undefined") {
        sessionStorage.setItem("loginType", "Reveal price");
        localStorage.setItem(
          "MMV",
          JSON.stringify({
            make: brand_name,
            model: model_name,
          })
        );
      }
      hideLoader();
      const currentUrl = addCaptureIntentIfNeeded(
        window.location.href,
        { make: brand_name, model: model_name },
        "Reveal price",
        pageName as any
      );
      await handleLoginFlow(getLoginUrl(currentUrl), LoginSdkConfig.AD_PRICE);
    }
  };

  const formatSpecifications = (specs: string[]) => {
    if (!specs || specs.length === 0) return "";
    return specs.join(" • ");
  };

  return (
    <div
      ref={contentRef}
      className={styles.cardContent}
      data-content-header
      style={!isMobile && minHeight > 0 ? { minHeight: `${minHeight}px` } : {}}
    >
      {/* {
        experts_rating && (
          <div className={styles.rating}>
            <Image
              src={`${assetLoader()}/icons/RatingStar.svg`}
              alt="rating star"
              width={12}
              height={12}
              loading="eager"
            />
            <Text className={styles.ratingText}>
              {BUYCARCARD_CONFIG.rating.text(experts_rating)}
            </Text>
          </div>
        )
      } */}
      <div className={styles.contentHeaderContainer}>
        <div className={styles.contentHeaderWrapper}>
          <div className={styles.contentHeader}>
            <Link
              href={`/cars/${model_slug || ""}`}
              onClick={handleRouteClick}
              className={styles.carName}
            >
              {`${brand_name} ${model_name}`}
            </Link>
            {variant_count && (
              <Text className={styles.carVariants}>
                {BUYCARCARD_CONFIG.variants.text(variant_count)}
              </Text>
            )}
          </div>
          {fuel_types && transmission_types && (
            <div className={styles.contentSpecifications}>
              {fuel_types && fuel_types.length > 0 && (
                <Text className={styles.specificationItem}>
                  {formatSpecifications(fuel_types)}
                </Text>
              )}
              {transmission_types && transmission_types.length > 0 && (
                <Text className={styles.specificationItem}>
                  {formatSpecifications(transmission_types)}
                </Text>
              )}
            </div>
          )}
        </div>

        <div className={styles.priceSection}>
          <Text className={styles.priceLabel}>
            {status === "upcoming"
              ? `Estimated on-road price in ${cityName}`
              : !isLoggedIn ||
                status === "out_of_production" ||
                status === "non_sellable"
              ? `On-road price in ${cityName}`
              : BUYCARCARD_CONFIG.price.loggedIn.label}
          </Text>

          {isLoggedIn ? (
            <div className={styles.loggedInPriceContainer}>
              <div className={styles.priceWithStrikethrough}>
                <Text className={styles.priceRange}>
                  {!isLoggedIn ||
                  ["out_of_production", "non_sellable", "upcoming"].includes(
                    status
                  )
                    ? BUYCARCARD_CONFIG.price.loggedOut.formatRange(
                        min_price,
                        max_price
                      )
                    : BUYCARCARD_CONFIG.price.loggedIn.format(min_price)}
                </Text>
                {min_on_road_price && (
                  <div className={styles.originalPriceContainer}>
                    <Text className={styles.originalPrice}>
                      {BUYCARCARD_CONFIG.price.loggedIn.originalPrice(
                        min_on_road_price
                      )}
                    </Text>
                    <div className={styles.strikethroughLine}></div>
                  </div>
                )}
                {min_acko_drive_savings && (
                  <Text className={styles.savingsTextLoggedIn}>
                    {BUYCARCARD_CONFIG.price.loggedIn.savings(
                      min_acko_drive_savings
                    )}
                  </Text>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.loggedOutPriceContainer}>
              <Text className={styles.priceRange}>
                {BUYCARCARD_CONFIG.price.loggedOut.formatRange(
                  min_price,
                  max_price
                )}
              </Text>
              {min_acko_drive_savings > 0 &&
                status !== "out_of_production" &&
                status !== "non_sellable" &&
                status !== "upcoming" && (
                  <Text className={styles.savingsText}>
                    {BUYCARCARD_CONFIG.price.loggedOut.savings(
                      min_acko_drive_savings
                    )}
                  </Text>
                )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.contentButtons}>
        <Button
          onClick={handleButtonclick}
          className={
            isLoggedIn
              ? styles.contentLoggedInButton
              : styles.contentLoggedOutButton
          }
        >
          {
            isCampaignPage ? "Buy on ACKO Drive" : status === 'out_of_production' || status === 'non_sellable' || status === 'upcoming'
            ? BUYCARCARD_CONFIG.buttons.loggedIn.text
            : isLoggedIn
            ? BUYCARCARD_CONFIG.buttons.loggedIn.text
            : BUYCARCARD_CONFIG.buttons.loggedOut.text}
        </Button>
        <WishlistButton
          isLoggedIn={isLoggedIn}
          isInWishlist={isInWishlist}
          onWishlistClick={onWishlistClick}
          className={styles.wishlistButton}
          brand_name={brand_name}
          model_name={model_name}
          cityName={cityName}
          status={status}
          title={title}
          isCampaignPage={isCampaignPage}
        />
      </div>
    </div>
  );
};

export default CardContent;
